import {
  combineReducers,
  configureStore,
  createDynamicMiddleware,
  Middleware,
  Reducer,
  ReducersMapObject,
} from '@reduxjs/toolkit';
import loginReducer from './slices/login';
import drawerReducer from './slices/drawerToggle';
import { loginAPI } from '../app/pages/login/services/services';
import { downloadFilePage } from '../app/modules/user-reports/download-page/services/services';
import reconciliationReducer from './slices/reconciliation';
import refreshExpiredReducer from './slices/refreshExpiredReducer';
import customFilterReducer from './slices/customFilter';
import tabReducer from './slices/tabControl';

export type InjectableApi = {
  reducerPath: string;
  reducer: Reducer;
  middleware: Middleware;
};

const staticReducers = {
  [loginAPI.reducerPath]: loginAPI.reducer,
  [downloadFilePage.reducerPath]: downloadFilePage.reducer,
  login: loginReducer,
  drawer: drawerReducer,
  reconciliation: reconciliationReducer,
  refreshExpired: refreshExpiredReducer,
  tabReducer: tabReducer,
  customFilter: customFilterReducer,
};

const asyncReducers: ReducersMapObject = {};
const injectedApiPaths = new Set<string>([
  loginAPI.reducerPath,
  downloadFilePage.reducerPath,
]);
const dynamicMiddleware = createDynamicMiddleware();

function createRootReducer() {
  return combineReducers({
    ...staticReducers,
    ...asyncReducers,
  });
}

const store = configureStore({
  reducer: createRootReducer(),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          'downloadFilePage/executeMutation',
          'downloadFilePage/executeMutation/pending',
          'downloadFilePage/executeMutation/fulfilled',
          'downloadFilePage/executeMutation/rejected',
          'commonAPI/executeQuery/fulfilled',
          'walletUserAPI/executeQuery',
          'walletUserAPI/executeQuery/fulfilled',
        ],
        ignoredActionPaths: [
          'payload',
          'meta.arg',
          'meta.baseQueryMeta',
        ],
        ignoredPaths: [
          'downloadFilePage.mutations',
          `${downloadFilePage.reducerPath}.mutations`,
          'walletUserAPI.queries',
          'walletUserAPI.mutations',
          'commonAPI.queries'
        ],
      },
    })
      .prepend(dynamicMiddleware.middleware)
      .concat(loginAPI.middleware, downloadFilePage.middleware),
});

export function injectApi(api: InjectableApi): void {
  if (injectedApiPaths.has(api.reducerPath)) {
    return;
  }
  injectedApiPaths.add(api.reducerPath);
  asyncReducers[api.reducerPath] = api.reducer;
  store.replaceReducer(createRootReducer());
  dynamicMiddleware.addMiddleware(api.middleware);
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
